import { clientBrainActionSchema } from "../../features/clientBrainActions/models";
import { z } from "zod";
import { sourceSelectorSchema, dataScopeSchema, workflowStatisticsQuerySchema, workflowDataQuerySchema } from "../../features/workflowData/models";

export const MAX_DESCRIPTION_LENGTH = 30000;
// Trigger text is sent with every note classification, once per workflow, so it stays short.
export const MAX_TRIGGER_DESCRIPTION_LENGTH = 6000;
export const MAX_TITLE_LENGTH = 80;
export const MAX_WORKFLOW_NODE_TEXT_LENGTH = 60000;
export const MAX_DOCUMENT_NODE_TEXT_LENGTH = 500000;
export const MAX_OUTPUT_OPTIONS = 100;
export const MAX_OUTPUT_OPTION_VALUE_LENGTH = 100;
export const MAX_OUTPUT_OPTION_LABEL_LENGTH = 200;

const nonblankOptionText = (maximum: number) => z.string().min(1).max(maximum).refine(value => Boolean(value.trim()), "Dropdown options must not be blank");
export const workflowContextSelectorSchema = sourceSelectorSchema;
const outputFields = {
    client_brain_action: clientBrainActionSchema.nullish(),
    analytics: workflowStatisticsQuerySchema.nullish(),
    context_selectors: z.array(workflowContextSelectorSchema).max(5).refine(values => new Set(values).size === values.length, "Context sources must be unique").optional(),
    output_type: z.enum(["text", "select"]).optional(),
    options: z.array(z.object({
        value: nonblankOptionText(MAX_OUTPUT_OPTION_VALUE_LENGTH),
        label: nonblankOptionText(MAX_OUTPUT_OPTION_LABEL_LENGTH)
    }).strict()).max(MAX_OUTPUT_OPTIONS).optional()
};

function validateOutputConfiguration(
    value: { output_type?: "text" | "select"; options?: Array<{ value: string; label: string }>; context_selectors?: string[]; analytics?: unknown },
    context: z.RefinementCtx
): void {
    if (value.analytics && (!(value.context_selectors ?? []).includes("notes.signed") || (value.output_type ?? "text") !== "text")) context.addIssue({ code: z.ZodIssueCode.custom, path: ["analytics"], message: "Statistics require Note History and a text result" });
    const options = value.options ?? [];
    if (value.output_type === "select" && options.length < 2) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "A dropdown requires at least two explicit options" });
    }
    if ((value.output_type ?? "text") === "text" && options.length > 0) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "Text boxes cannot have dropdown options" });
    }
    if (new Set(options.map(option => option.value)).size !== options.length) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ["options"], message: "Dropdown option values must be unique" });
    }
}

export const workflowOutputConfigurationSchema = z.object(outputFields).superRefine(validateOutputConfiguration);

const descriptionSchema = z.string().max(MAX_DESCRIPTION_LENGTH);
const triggerDescriptionSchema = z.string().max(MAX_TRIGGER_DESCRIPTION_LENGTH);
const titleSchema = z.string().max(MAX_TITLE_LENGTH);
const displayModeSchema = z.enum(["content", "source_links"]);
const membershipIdSchema = z
    .string()
    .min(1)
    .max(200)
    .refine(value => value === value.trim());
const membershipIdsSchema = z
    .array(membershipIdSchema)
    .max(50)
    .refine(values => new Set(values).size === values.length, "Select each member only once per role");
const legacyBoxTaskSettingsSchema = z.object({
    permissionVersion: z.never().optional(),
    responsibleMemberIds: membershipIdsSchema,
    signerMemberIds: membershipIdsSchema,
    notificationRecipients: z
        .array(z.object({ memberId: membershipIdSchema, email: z.string().max(320) }))
        .max(50)
        .refine(values => new Set(values.map(value => value.memberId)).size === values.length, "Select each notification recipient only once")
}).strict();

export const boxPermissionSettingsSchema = z
    .object({
        permissionVersion: z.literal(2),
        signerMemberIds: membershipIdsSchema,
        viewerMemberIds: membershipIdsSchema
    })
    .strict();

export const boxTaskSettingsSchema = z.union([boxPermissionSettingsSchema, legacyBoxTaskSettingsSchema]);

export const triggerSchema = z.object({
    id: z.string().min(1),
    title: titleSchema.optional(),
    description: triggerDescriptionSchema
});

export const stepSchema = z.object({
    ...outputFields,
    task_settings: boxTaskSettingsSchema.optional(),
    id: z.string().min(1),
    title: titleSchema.optional(),
    description: descriptionSchema,
    display_mode: displayModeSchema.optional()
});

export const outcomeSchema = z.object({
    ...outputFields,
    task_settings: boxTaskSettingsSchema.optional(),
    id: z.string().min(1),
    title: titleSchema.optional(),
    description: descriptionSchema,
    display_mode: displayModeSchema.optional()
});

export const subflowSchema = z.object({
    ...outputFields,
    task_settings: boxTaskSettingsSchema.optional(),
    id: z.string().min(1),
    title: titleSchema.optional(),
    description: descriptionSchema,
    display_mode: displayModeSchema.optional(),
    steps: z.array(stepSchema)
});

export const noteWorkflowSchema = z.object({
    timezone: z.string().optional(),
    data_query: workflowDataQuerySchema.nullish(),
    data_scope: dataScopeSchema.nullish(),
    id: z.string().min(1),
    view_all_boxes: z.boolean().optional(),
    trigger: triggerSchema,
    steps: z.array(stepSchema),
    outcomes: z.array(outcomeSchema),
    subflows: z.array(subflowSchema).optional()
});

export const noteWorkflowsDocumentSchema = z.object({
    schema_version: z.number().int(),
    workflows: z.array(noteWorkflowSchema),
    updated_at: z.number().int().optional(),
    updated_by: z.string().optional()
}).superRefine((document, context) => {
    for (const [workflowIndex, workflow] of document.workflows.entries()) {
        const groups = [workflow.steps, workflow.outcomes, workflow.subflows ?? []];
        const names = ["steps", "outcomes", "subflows"];
        groups.forEach((nodes, groupIndex) => nodes.forEach((node, index) => {
            const result = workflowOutputConfigurationSchema.safeParse(node);
            if (!result.success) result.error.issues.forEach(issue => context.addIssue({
                ...issue, path: ["workflows", workflowIndex, names[groupIndex], index, ...issue.path]
            }));
        }));
        (workflow.subflows ?? []).forEach((subflow, subflowIndex) => subflow.steps.forEach((node, index) => {
            const result = workflowOutputConfigurationSchema.safeParse(node);
            if (!result.success) result.error.issues.forEach(issue => context.addIssue({
                ...issue, path: ["workflows", workflowIndex, "subflows", subflowIndex, "steps", index, ...issue.path]
            }));
        }));
    }
});

export function clampDescription(value: string): string {
    return value.slice(0, MAX_DESCRIPTION_LENGTH);
}

export function clampTriggerDescription(value: string): string {
    return value.slice(0, MAX_TRIGGER_DESCRIPTION_LENGTH);
}

export function clampTitle(value: string): string {
    return value.slice(0, MAX_TITLE_LENGTH);
}

export function validateNoteWorkflowsDocument(document: unknown): string | null {
    const parsed = noteWorkflowsDocumentSchema.safeParse(document);
    if (!parsed.success) {
        return parsed.error.issues[0]?.message ?? "Invalid note workflows document";
    }

    if (parsed.data.schema_version !== 1) {
        return `Unsupported schema_version: ${parsed.data.schema_version}`;
    }

    const seen = new Set<string>();
    let documentTextLength = 0;

    for (const workflow of parsed.data.workflows) {
        const outputNodes = [...workflow.steps, ...workflow.outcomes, ...(workflow.subflows ?? []),
            ...(workflow.subflows ?? []).flatMap(subflow => subflow.steps)];
        const workflowTextLength = (workflow.trigger.title?.length ?? 0) + workflow.trigger.description.length + outputNodes.reduce(
            (total, node) => total + (node.title?.length ?? 0) + node.description.length +
                (node.options ?? []).reduce((optionTotal, option) => optionTotal + option.value.length + option.label.length, 0), 0
        );
        if (workflowTextLength > MAX_WORKFLOW_NODE_TEXT_LENGTH) {
            return `Workflow ${workflow.id} exceeds ${MAX_WORKFLOW_NODE_TEXT_LENGTH} characters of node text`;
        }
        documentTextLength += workflowTextLength;
        if (seen.has(workflow.id)) {
            return `Duplicate workflow id: ${workflow.id}`;
        }
        seen.add(workflow.id);

        const nodeIds = [
            workflow.trigger.id,
            ...workflow.steps.map(step => step.id),
            ...workflow.outcomes.map(outcome => outcome.id),
            ...(workflow.subflows ?? []).flatMap(subflow => [subflow.id, ...subflow.steps.map(step => step.id)])
        ];

        for (const id of nodeIds) {
            if (seen.has(id)) {
                return `Duplicate node id: ${id}`;
            }
            seen.add(id);
        }

        for (const step of workflow.steps) {
            if (!step.description.trim()) {
                return `Step ${step.id} description cannot be empty`;
            }
        }

        for (const outcome of workflow.outcomes) {
            if (!outcome.description.trim()) {
                return `Outcome ${outcome.id} description cannot be empty`;
            }
        }

        for (const subflow of workflow.subflows ?? []) {
            if (!subflow.steps.length) {
                return `Subflow ${subflow.id} must have at least one step`;
            }
            for (const step of subflow.steps) {
                if (!step.description.trim()) {
                    return `Subflow step ${step.id} description cannot be empty`;
                }
            }
        }
    }

    if (documentTextLength > MAX_DOCUMENT_NODE_TEXT_LENGTH) {
        return `Note workflows exceed ${MAX_DOCUMENT_NODE_TEXT_LENGTH} characters of node text`;
    }
    return null;
}
